import { Box, Grid, Typography, Paper, Tooltip } from '@mui/material'
import ComputerIcon from '@mui/icons-material/Computer'
import WifiIcon from '@mui/icons-material/Wifi'
import WifiOffIcon from '@mui/icons-material/WifiOff'
import LoopIcon from '@mui/icons-material/Loop'
import SignalWifiBadIcon from '@mui/icons-material/SignalWifiBad'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { useAppTranslation } from 'src/admin/core/hooks'
import { SummaryData } from 'src/admin/core/services/screens/screenDashboardService'

interface SummaryCardsProps {
  data: SummaryData
}

interface CardConfig {
  labelKey: string
  value: number
  icon: React.ReactNode
  color: string
  bgColor?: string
  textColor?: string
  tooltipText?: string
}

const SummaryCards = ({ data }: SummaryCardsProps) => {
  const { label } = useAppTranslation()

  const cards: CardConfig[] = [
    {
      labelKey: 'TOTAL_SCREENS',
      value: data.total_screens,
      icon: <ComputerIcon sx={{ fontSize: 18 }} />,
      color: 'text.secondary',
    },
    {
      labelKey: 'ONLINE',
      value: data.online,
      icon: <WifiIcon sx={{ fontSize: 18 }} />,
      color: '#22c55e',
    },
    {
      labelKey: 'OFFLINE',
      value: data.offline,
      icon: <WifiOffIcon sx={{ fontSize: 18 }} />,
      color: '#ef4444',
    },
    {
      labelKey: 'CRASH_LOOP',
      value: data.crash_loop,
      icon: <LoopIcon sx={{ fontSize: 18 }} />,
      color: '#ef4444',
      tooltipText: 'La pantalla presenta reinicios constantes',
    },
    {
      labelKey: 'INACTIVATED',
      value: data.disconnected,
      icon: <SignalWifiBadIcon sx={{ fontSize: 18 }} />,
      color: 'text.primary',
      tooltipText: 'Agencias o pantallas inactivas',
    },
  ]

  if (data.db_error_agencies > 0) {
    cards.push({
      labelKey: 'DB_ERROR_AGENCIES',
      value: data.db_error_agencies,
      icon: <WarningAmberIcon sx={{ fontSize: 18 }} />,
      color: '#ffffff',
      bgColor: '#dc2626',
      textColor: '#ffffff',
    })
  }

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((card) => {
        const cardContent = (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: card.bgColor ? 'transparent' : 'divider',
              bgcolor: card.bgColor || 'background.paper',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              transition: 'box-shadow 0.2s ease',
              '&:hover': { boxShadow: 3 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ color: card.color, display: 'flex', alignItems: 'center' }}>{card.icon}</Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  fontWeight: 600,
                  fontSize: '0.62rem',
                  color: card.textColor || 'text.secondary',
                  lineHeight: 1.2,
                }}
              >
                {label(card.labelKey as any)}
              </Typography>
            </Box>
            <Typography
              variant="h3"
              component="p"
              sx={{
                fontWeight: 800,
                fontSize: '2rem',
                color: card.textColor || card.color,
                lineHeight: 1,
                mt: 0.5,
              }}
            >
              {card.value}
            </Typography>
          </Paper>
        )

        return (
          <Grid item xs={6} sm={4} md key={card.labelKey}>
            {card.tooltipText ? (
              <Tooltip title={card.tooltipText} placement="bottom" arrow>
                {cardContent}
              </Tooltip>
            ) : (
              cardContent
            )}
          </Grid>
        )
      })}
    </Grid>
  )
}

export default SummaryCards
